import { useState, useRef } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

function CampaignInformation({ onNext, onBack }) {
  const [formData, setFormData] = useState({
    title: 'Early Black Friday',
    startDate: '2024-11-05T12:00',
    endDate: '2024-11-10T12:00',
    status: 'To Do',
    assignee: '',
    category: 'Campaign',
    topic: 'Share recipe cards featuring your products for a festive Thanksgiving experience!',
    emailTemplate: 'YuIujOvF4YALkGECFbrN',
    discount: {
      enabled: true,
      type: 'Order Discount',
      method: 'Code',
      code: '25OFF',
      valueType: 'Percentage',
      value: '25'
    },
    collections: [],
    bannerTemplate: '',
    media: null
  });

  const [expandedSections, setExpandedSections] = useState({
    collections: false,
    discount: false,
    emailTemplate: false,
    bannerTemplate: false,
    media: false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onNext({ information: formData });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleDiscountChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      discount: {
        ...prev.discount,
        [field]: value
      }
    }));
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-6">Campaign Information</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Information Section */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="datetime-local"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="datetime-local"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option>To Do</option>
                <option>In Progress</option>
                <option>Done</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
              <select
                name="assignee"
                value={formData.assignee}
                onChange={handleChange}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option value="">Unassigned</option>
                <option value="user1">User 1</option>
                <option value="user2">User 2</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <div className="flex items-center space-x-2 h-[38px]">
                <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                  🎯 Campaign
                </span>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Got a topic?</label>
            <div className="relative">
              <textarea
                name="topic"
                value={formData.topic}
                onChange={handleChange}
                rows={4}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
              <div className="absolute top-2 right-2">
                <div className="flex items-center space-x-1 bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                  <span>✓</span>
                  <span>Selected</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Email Template Section */}
        <div className="border border-dashed border-purple-300 rounded-lg">
          <button
            type="button"
            onClick={() => toggleSection('emailTemplate')}
            className="w-full flex items-center justify-between p-4"
          >
            <div className="flex items-center space-x-2">
              <span className="text-lg font-medium">Email Template</span>
              <span className="text-sm text-gray-500">(Optional)</span>
            </div>
            <ChevronDownIcon className={`w-5 h-5 transform transition-transform ${expandedSections.emailTemplate ? 'rotate-180' : ''}`} />
          </button>
          {expandedSections.emailTemplate && (
            <div className="p-4 border-t border-purple-300">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search for a template"
                  className="w-full pl-10 rounded-lg border-gray-300"
                />
              </div>
            </div>
          )}
        </div>

        {/* Homepage Banner Template Section */}
        <div className="border border-dashed border-purple-300 rounded-lg">
          <button
            type="button"
            onClick={() => toggleSection('bannerTemplate')}
            className="w-full flex items-center justify-between p-4"
          >
            <div className="flex items-center space-x-2">
              <span className="text-lg font-medium">Homepage Banner Template</span>
              <span className="text-sm text-gray-500">(Optional)</span>
            </div>
            <ChevronDownIcon className={`w-5 h-5 transform transition-transform ${expandedSections.bannerTemplate ? 'rotate-180' : ''}`} />
          </button>
          {expandedSections.bannerTemplate && (
            <div className="p-4 border-t border-purple-300">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search for a template"
                  className="w-full pl-10 rounded-lg border-gray-300"
                />
              </div>
            </div>
          )}
        </div>

        {/* Discount Section */}
        <div className="border border-dashed border-purple-300 rounded-lg">
          <button
            type="button"
            onClick={() => toggleSection('discount')}
            className="w-full flex items-center justify-between p-4"
          >
            <div className="flex items-center space-x-2">
              <span className="text-lg font-medium">Discount</span>
              <span className="text-sm text-gray-500">(Optional)</span>
            </div>
            <ChevronDownIcon className={`w-5 h-5 transform transition-transform ${expandedSections.discount ? 'rotate-180' : ''}`} />
          </button>
          {expandedSections.discount && (
            <div className="p-4 border-t border-purple-300 space-y-4">
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    checked={formData.discount.enabled}
                    onChange={(e) => handleDiscountChange('enabled', e.target.checked)}
                    className="text-indigo-600"
                  />
                  <span>Yes</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    checked={!formData.discount.enabled}
                    onChange={(e) => handleDiscountChange('enabled', !e.target.checked)}
                    className="text-indigo-600"
                  />
                  <span>No</span>
                </label>
              </div>
              {formData.discount.enabled && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      value={formData.discount.type}
                      onChange={(e) => handleDiscountChange('type', e.target.value)}
                      className="w-full rounded-lg border-gray-300"
                    >
                      <option>Order Discount</option>
                      <option>Product Discount</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Method</label>
                      <select
                        value={formData.discount.method}
                        onChange={(e) => handleDiscountChange('method', e.target.value)}
                        className="w-full rounded-lg border-gray-300"
                      >
                        <option>Code</option>
                        <option>Automatic</option>
                      </select>
                    </div>
                    {formData.discount.method === 'Code' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                        <input
                          type="text"
                          value={formData.discount.code}
                          onChange={(e) => handleDiscountChange('code', e.target.value)}
                          className="w-full rounded-lg border-gray-300"
                        />
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                      <select
                        value={formData.discount.valueType}
                        onChange={(e) => handleDiscountChange('valueType', e.target.value)}
                        className="w-full rounded-lg border-gray-300"
                      >
                        <option>Percentage</option>
                        <option>Fixed Amount</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">&nbsp;</label>
                      <div className="flex items-center">
                        <input
                          type="text"
                          value={formData.discount.value}
                          onChange={(e) => handleDiscountChange('value', e.target.value)}
                          className="w-full rounded-lg border-gray-300"
                        />
                        <span className="ml-2">%</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Collections Section */}
        <div className="border border-dashed border-purple-300 rounded-lg">
          <button
            type="button"
            onClick={() => toggleSection('collections')}
            className="w-full flex items-center justify-between p-4"
          >
            <div className="flex items-center space-x-2">
              <span className="text-lg font-medium">Collections</span>
              <span className="text-sm text-gray-500">(Optional)</span>
            </div>
            <ChevronDownIcon className={`w-5 h-5 transform transition-transform ${expandedSections.collections ? 'rotate-180' : ''}`} />
          </button>
          {expandedSections.collections && (
            <div className="p-4 border-t border-purple-300">
              <div className="text-sm mb-4">Select up to 5 collections</div>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search Collections"
                  className="w-full pl-10 rounded-lg border-gray-300"
                />
              </div>
            </div>
          )}
        </div>

        {/* Media Section */}
        <div className="border border-dashed border-purple-300 rounded-lg">
          <button
            type="button"
            onClick={() => toggleSection('media')}
            className="w-full flex items-center justify-between p-4"
          >
            <div className="flex items-center space-x-2">
              <span className="text-lg font-medium">Media</span>
              <span className="text-sm text-gray-500">(Optional)</span>
            </div>
            <ChevronDownIcon className={`w-5 h-5 transform transition-transform ${expandedSections.media ? 'rotate-180' : ''}`} />
          </button>
          {expandedSections.media && (
            <div className="p-4 border-t border-purple-300">
              <h3 className="text-base font-medium mb-4">Step 1: Upload Hero Image</h3>
              <div className="mt-2 flex justify-center rounded-lg border-2 border-dashed border-gray-300 px-6 py-10">
                <div className="text-center">
                  <div className="mt-4 flex text-sm leading-6 text-gray-600">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500"
                    >
                      <span>Upload a file</span>
                      <input id="file-upload" name="file-upload" type="file" className="sr-only" />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs leading-5 text-gray-600">PNG, JPG, GIF up to 10MB</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-4">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
            >
              Back
            </button>
          )}
          <button
            type="submit"
            className="px-4 py-2 bg-[#0049ac] text-white rounded-lg hover:bg-[#0049ac]/90"
          >
            Next
          </button>
        </div>
      </form>
    </div>
  );
}

export default CampaignInformation;